import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  Header,
  Modal,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react";

import { RootState } from "../../app/rootReducer";
import { isNoFilterApplied } from "../../searchSideBar/queryStringHelpers";
import { forEachLeafFilter } from "../../searchSideBar/propertyFilterTree";
import {
  parsePropertyFilterFromQueryString,
  PropertyFilterGroup,
  setQueryPropertyFilters,
  setQueryPropertyFiltersForceRehydrate,
  setQueryString,
  sumPropertyFilterQueries,
} from "../../searchSideBar/searchSideBarConnector";
import { setValidationPageNumber } from "../asinValidationTableConnector";
import {
  BarKey,
  STATUS_ATTRIBUTE_SPECS,
  StackedStatusBarConfig,
  StatusSegment,
  buildAllStackedStatusBarConfigsFromAggregation,
  isBarChipPropertyKey,
  parseFilterChip,
} from "./chartData";
import {
  stripValidationStatusFilters,
  toggleStatusFilterToken,
} from "./filterTokenToggle";
import { StackedStatusBar } from "./StackedStatusBar";
import { fetchStatusOverviewAggregation } from "./statusOverviewAggregationConnector";
import { useStatusOverviewPrefs } from "./useStatusOverviewPrefs";

export const DEFAULT_VISIBILITY: Record<BarKey, boolean> = {
  overall: true,
  lvs: true,
  hattrick: true,
  gtm: false,
  dgl: false,
  rl: false,
  instock: false,
  ox: false,
  cs: false,
};

const BAR_LABELS: Record<BarKey, string> = {
  overall: "Validation Status",
  lvs: "LVS Status",
  hattrick: "Hattrick Status",
  gtm: "GTM Status",
  dgl: "DGL Status",
  rl: "RL Status",
  instock: "Instock Status",
  ox: "OX Status",
  cs: "CS Status",
};

function formatEarliestValidation(ms: number | undefined): string | undefined {
  if (ms === undefined) return undefined;
  try {
    return new Date(ms).toISOString().slice(0, 16).replace("T", " ") + " UTC";
  } catch {
    return undefined;
  }
}

export const StatusOverview: React.FC = () => {
  const dispatch = useDispatch();

  const queryString = useSelector(
    (state: RootState) => state.generateQuery.queryString
  );
  const queryPropertyFilters = useSelector(
    (state: RootState) => state.generateQuery.queryPropertyFilters
  );
  // Flipped whenever a submit / save wants dependent views to refetch
  // (e.g. after bulk pass-exception submission). Watching it here lets
  // the charts stay in sync with the table without a page reload.
  const refreshFlipper = useSelector(
    (state: RootState) => state.asinValidationTableControl.refreshFlipper
  );
  const {
    aggregationResults,
    totalCount,
    status,
    error,
    earliestValidationRunAtMs,
  } = useSelector((state: RootState) => state.statusOverviewAggregation);

  const { visibleBars, toggleBar } = useStatusOverviewPrefs();
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // The bars aggregate against a "baseline" query that omits any
  // active status chips. This keeps chart-driven drill-down (and
  // manually authored status chips via the FilterBar dropdown) from
  // reshaping the bars they were selected from — clicks only
  // highlight; the table below still filters normally.
  //
  // When the baseline collapses to `(all)` (the only applied filter
  // is a status chip), fall back to the full `queryString` instead of
  // aggregating against `(all)`. The bars then show the distribution
  // across the filtered subset, which is what the user asked for
  // rather than a full-tenant scan.
  //
  // Derive from the applied `queryString` (updated only on Submit /
  // bar-click / preset apply) rather than the staged
  // `queryPropertyFilters` (updated on every chip keystroke). Deriving
  // from staged chips would refetch the aggregation on each edit
  // before the user submits.
  const baselineQueryString = useMemo(() => {
    if (queryString === undefined || isNoFilterApplied(queryString)) {
      return "";
    }
    const parsed = parsePropertyFilterFromQueryString(queryString);
    if (parsed === undefined) {
      // Query can't be represented as a property-filter tree; can't
      // strip status chips, so aggregate against the full query.
      return queryString;
    }
    const stripped = sumPropertyFilterQueries(
      stripValidationStatusFilters(parsed)
    ).toString();
    return isNoFilterApplied(stripped) ? queryString : stripped;
  }, [queryString]);

  const zeroState = isNoFilterApplied(baselineQueryString);

  useEffect(() => {
    if (zeroState) {
      return;
    }
    dispatch(fetchStatusOverviewAggregation(baselineQueryString));
  }, [dispatch, baselineQueryString, zeroState, refreshFlipper]);

  const configs: readonly StackedStatusBarConfig[] = useMemo(
    () =>
      zeroState
        ? []
        : buildAllStackedStatusBarConfigsFromAggregation(
            aggregationResults,
            totalCount
          ),
    [aggregationResults, totalCount, zeroState]
  );

  // Derived from every chip whose `propertyKey` could originate from a
  // Status Overview bar (compound `validationStatus` chips and direct
  // chips like `validationOverallStatus`), so removing a chip via the
  // FilterBar `×` automatically clears the corresponding bar highlight.
  const activeStatusValuesByAttributeKey = useMemo<
    ReadonlyMap<string, ReadonlySet<string>>
  >(() => {
    const result = new Map<string, Set<string>>();
    if (queryPropertyFilters === undefined) {
      return result;
    }
    // Visit every leaf at any depth. Legacy / shared URLs parse into
    // multiple sibling groups (date range + productLineId OR +
    // programId + …) and the chart-click writer appends the new chip
    // as its own sibling group, so status chips can live anywhere in
    // the tree.
    forEachLeafFilter(queryPropertyFilters, (f) => {
      if (typeof f.value !== "string" || !isBarChipPropertyKey(f.propertyKey)) {
        return;
      }
      const parsed = parseFilterChip(f.propertyKey, f.value);
      if (parsed === undefined) {
        return;
      }
      let bucket = result.get(parsed.attributeKey);
      if (bucket === undefined) {
        bucket = new Set<string>();
        result.set(parsed.attributeKey, bucket);
      }
      bucket.add(parsed.statusValue);
    });
    return result;
  }, [queryPropertyFilters]);

  const hasAnyActiveStatusFilter = activeStatusValuesByAttributeKey.size > 0;

  const onSegmentClick = useCallback(
    (segment: StatusSegment) => {
      const nextFilters: PropertyFilterGroup = toggleStatusFilterToken(
        queryPropertyFilters,
        segment
      );
      dispatch(setQueryPropertyFilters(nextFilters));
      dispatch(setQueryPropertyFiltersForceRehydrate(true));
      dispatch(setQueryString());
      dispatch(setValidationPageNumber({ pageNumber: 1 }));
    },
    [dispatch, queryPropertyFilters]
  );

  // Strip every chip that could originate from a bar (compound
  // `validationStatus` chips and direct chips like
  // `validationOverallStatus`) while preserving the rest.
  const onClearChartFilter = useCallback(() => {
    if (!hasAnyActiveStatusFilter) {
      return;
    }
    const nextFilters = stripValidationStatusFilters(queryPropertyFilters);
    dispatch(setQueryPropertyFilters(nextFilters));
    dispatch(setQueryPropertyFiltersForceRehydrate(true));
    dispatch(setQueryString());
    dispatch(setValidationPageNumber({ pageNumber: 1 }));
  }, [dispatch, hasAnyActiveStatusFilter, queryPropertyFilters]);

  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  const gearButton = (
    <Button
      data-testid="status-overview-gear"
      variant="icon"
      iconName="settings"
      ariaLabel="Open status overview preferences"
      onClick={openPreferences}
    />
  );

  const earliestValidationLabel = zeroState
    ? undefined
    : formatEarliestValidation(earliestValidationRunAtMs);

  const headerActions = (
    <SpaceBetween size="xs" direction="horizontal" alignItems="center">
      {earliestValidationLabel !== undefined && (
        <Box data-testid="status-overview-earliest-validation" variant="small">
          Earliest validation: {earliestValidationLabel}
        </Box>
      )}
      {hasAnyActiveStatusFilter && (
        <Button
          data-testid="status-overview-clear-chart-filter"
          variant="link"
          onClick={onClearChartFilter}
        >
          Clear chart filter
        </Button>
      )}
      {gearButton}
    </SpaceBetween>
  );

  const preferencesModal = (
    <Modal
      data-testid="status-overview-preferences-modal"
      visible={preferencesOpen}
      onDismiss={closePreferences}
      header="Status Overview Preferences"
      closeAriaLabel="Close preferences"
      footer={
        <Box float="right">
          <Button variant="primary" onClick={closePreferences}>
            Done
          </Button>
        </Box>
      }
    >
      <SpaceBetween size="s">
        <Box variant="strong">Select statuses to display</Box>
        <SpaceBetween size="xs">
          {STATUS_ATTRIBUTE_SPECS.map((spec) => (
            <Checkbox
              key={spec.key}
              data-testid={`status-overview-preference-${spec.key}`}
              checked={visibleBars[spec.key]}
              onChange={() => toggleBar(spec.key)}
            >
              {BAR_LABELS[spec.key]}
            </Checkbox>
          ))}
        </SpaceBetween>
      </SpaceBetween>
    </Modal>
  );

  if (zeroState) {
    return null;
  }

  const visibleConfigs = configs.filter((c) => visibleBars[c.key]);
  const columnCount = Math.max(1, Math.min(visibleConfigs.length, 4));
  const showLoading =
    status === "loading" && Object.keys(aggregationResults).length === 0;
  const showError = status === "error";

  return (
    <>
      <Container
        data-testid="status-overview"
        header={
          <Header variant="h2" actions={headerActions}>
            Status Overview
          </Header>
        }
      >
        {showError ? (
          <Box
            data-testid="status-overview-error"
            variant="p"
            color="text-status-error"
          >
            Could not load status overview: {error ?? "unknown error"}
          </Box>
        ) : showLoading ? (
          <Box
            data-testid="status-overview-loading"
            variant="p"
            color="text-body-secondary"
          >
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              <Spinner />
              <span>Loading status overview…</span>
            </SpaceBetween>
          </Box>
        ) : visibleConfigs.length === 0 ? (
          <Box variant="p" color="text-body-secondary">
            All status bars are hidden. Open preferences to show one.
          </Box>
        ) : (
          <ColumnLayout columns={columnCount} minColumnWidth={200}>
            {visibleConfigs.map((config) => (
              <StackedStatusBar
                key={config.key}
                config={config}
                onSegmentClick={onSegmentClick}
                activeStatusValues={activeStatusValuesByAttributeKey.get(
                  config.attributeKey
                )}
              />
            ))}
          </ColumnLayout>
        )}
      </Container>
      {preferencesModal}
    </>
  );
};

export default StatusOverview;
